import classNames from "classnames"
import { getChallengeThumbnail } from "./challengeMeta"

interface ChallengeItemProps {
  challenge: IChallenge
  index: number
  isSelected: boolean
  locked: boolean
  completed: boolean
  onClick: (index: number) => void
}

export default function ChallengeItem({ challenge, index, isSelected, locked, completed, onClick }: ChallengeItemProps) {
  const thumbnail = getChallengeThumbnail(index)
  const showCompleted = completed && !locked

  return (
    <button
      type="button"
      onClick={() => !locked && onClick(index)}
      aria-disabled={locked}
      disabled={locked}
      className={classNames(
        "group relative flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-all duration-200",
        "mobile:gap-[10px] mobile:rounded-[12px] mobile:p-[8px]",
        locked
          ? "cursor-not-allowed border-white/5 bg-white/[0.02]"
          : isSelected
            ? "border-amber-500 bg-amber-500/15"
            : showCompleted
              ? "border-emerald-500/40 bg-emerald-500/[0.07] hover:border-emerald-400/60 hover:bg-emerald-500/[0.12]"
              : "border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.07]"
      )}
    >
      {/* Active marker */}
      <span
        className={classNames(
          "absolute -left-px top-1/2 h-8 w-1 mobile:h-[30px] mobile:w-[3px] -translate-y-1/2 rounded-r-full bg-amber-400 transition-opacity duration-200",
          isSelected && !locked ? "opacity-100" : "opacity-0"
        )}
      />

      {/* Mission number */}
      <div className="flex w-9 mobile:w-[26px] flex-shrink-0 items-center justify-center">
        <span
          className={classNames(
            "font-fourth text-2xl mobile:text-[20px]/[1] font-bold leading-none",
            locked ? "text-white/20" : isSelected ? "text-amber-300" : "text-amber-400 group-hover:text-amber-300"
          )}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      {/* Thumbnail */}
      <div
        className={classNames(
          "relative h-14 w-14 mobile:h-[56px] mobile:w-[56px] flex-shrink-0 overflow-hidden rounded-lg mobile:rounded-[10px] bg-black/40 ring-1 ring-inset transition-all duration-200",
          locked ? "ring-white/5" : isSelected ? "ring-amber-400/60" : "ring-white/10 group-hover:ring-white/20"
        )}
      >
        <img
          src={thumbnail}
          alt=""
          className={classNames(
            "h-full w-full object-cover transition-transform duration-300",
            locked ? "grayscale brightness-[0.5]" : "group-hover:scale-105"
          )}
        />
        {locked ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <svg viewBox="0 0 24 24" className="h-6 w-6 mobile:h-[22px] mobile:w-[22px] text-amber-300/70" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
            </svg>
          </div>
        ) : showCompleted ? (
          <div className="absolute -right-1 -top-1 mobile:-right-[5px] mobile:-top-[5px] flex h-5 w-5 mobile:h-[19px] mobile:w-[19px] items-center justify-center rounded-full bg-emerald-500 ring-2 ring-gray-950">
            <svg viewBox="0 0 24 24" className="h-3 w-3 mobile:h-[11px] mobile:w-[11px] text-white" fill="none" stroke="currentColor" strokeWidth="3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : null}
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        {locked ? (
          <>
            <h3 className="font-fourth text-lg mobile:text-[15px]/[1.25] font-bold leading-tight text-white/40 line-clamp-2">{challenge.name}</h3>
            <span className="mt-1 mobile:mt-[5px] inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 mobile:px-[7px] py-0.5 mobile:py-[2px] text-[10px] mobile:text-[11px]/[1.3] font-semibold uppercase tracking-wider text-amber-300/80">
              Coming Soon
            </span>
          </>
        ) : (
          <>
            <h3 className="font-fourth text-lg mobile:text-[15px]/[1.25] font-bold leading-tight text-white line-clamp-2">{challenge.name}</h3>
            {showCompleted ? (
              <span className="mt-0.5 mobile:mt-[3px] inline-flex items-center gap-1 mobile:gap-[4px] text-[10px] mobile:text-[11px]/[1.3] font-semibold uppercase tracking-wider text-emerald-400">
                <svg viewBox="0 0 24 24" className="h-3 w-3 mobile:h-[11px] mobile:w-[11px]" fill="none" stroke="currentColor" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Completed
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* Right indicator */}
      {locked ? (
        <svg viewBox="0 0 24 24" className="h-5 w-5 mobile:h-[18px] mobile:w-[18px] flex-shrink-0 text-white/25" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
        </svg>
      ) : (
        <i
          className={classNames(
            "fas fa-chevron-right flex-shrink-0 text-xs mobile:text-[12px] transition-all duration-200",
            isSelected ? "text-amber-300" : showCompleted ? "text-emerald-400/50 group-hover:text-emerald-300" : "text-white/20 group-hover:text-white/50"
          )}
        />
      )}
    </button>
  )
}
